import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useRoutes } from 'react-router-dom'
import { selectFlow } from '@store/app'
import { pagesRoutes, pageGroups, type RoutesProps } from './pagesConfig'
import { useCustomNavigate, useSendEvents } from '@hooks'
import { EVENTS as E } from '../constants'
import { MainLayout } from '@applyft-web/ui-components'
import { Header } from '@components/Header'
import { Footer } from '@components/Footer'
import { getScreenPath } from '@utils'

export const RouteWithAppContainer = ({
  path,
  element,
  withButton = true,
  headerProps = undefined,
  withFooter = false,
  wrapperProps = {
    customStyles: {},
  },
  withoutWrap,
  // withProgressBar,
}: RoutesProps) => {
  const { pathname } = useLocation()
  const sendEvents = useSendEvents()
  const flow = useSelector(selectFlow)
  const [initEventSent, setInitEventStatus] = useState(false)
  const { withHeader, ...headerRest } = headerProps || { withHeader: false }
  const buttonHeight = withButton ? 72 : 0
  // const pt = Math.max(progressBarHeight + headerHeight, 16)
  const pt = withHeader ? 0 : 16
  const pb = withFooter ? 0 : 16 + buttonHeight
  const screenPath = getScreenPath(pathname)

  useEffect(() => {
    if (flow?.indexOf(screenPath) === 0) {
      if (initEventSent) return
      sendEvents(E.ONBOARDING_START)
      setInitEventStatus(true)
    }
  }, [sendEvents, initEventSent, flow, screenPath])

  if (withoutWrap) {
    return element
  }

  return (
    <MainLayout pt={pt} pb={pb} adaptive={true} {...wrapperProps}>
      {withHeader && <Header screenId={screenPath || path} {...headerRest} />}
      {element}
      {withFooter && <Footer />}
    </MainLayout>
  )
}

export const Router = () => {
  const { pathname, search, hash } = useLocation()
  const navigate = useCustomNavigate()

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1))
      if (el) {
        el.style.scrollMarginTop = '36px'
        el.scrollIntoView()
        return
      }
    }
    window.scrollTo(0, 0)
  }, [pathname, hash])

  useEffect(() => {
    const handleBackButton = (event: any) => {
      event.preventDefault()
      navigate(`${pathname}${search}`)
    }

    window.addEventListener('popstate', handleBackButton)

    return () => {
      window.removeEventListener('popstate', handleBackButton)
    }
  }, [pathname, search, navigate])

  const wrappedRoutes = pagesRoutes.map((route) => ({
    ...route,
    element: <RouteWithAppContainer {...route} />,
  }))

  const groupedRoutes = pageGroups.map(({ layout: { component: Layout, props }, children }) => ({
    element: <Layout {...props} />,
    children: children.map((route) => ({
      ...route,
      element: <RouteWithAppContainer {...route} />,
    })),
  }))

  return useRoutes([...wrappedRoutes, ...groupedRoutes])
}
